import type React from 'react';
import { memo, useCallback, useMemo, useState } from 'react';
import { faCaretRight } from '@fortawesome/pro-solid-svg-icons/faCaretRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { Button } from 'ui-components/components/button';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { Text } from 'ui-components/components/text/text';
import { TextType } from 'ui-components/components/text/text-type';
import { Tooltip } from 'ui-components/components/tooltip';
import { TooltipContent } from 'ui-components/components/tooltip/tooltip-content';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { fieldDescriptionMessages } from './field-description-messages';
import style from './field-replacement-sidebar.module.less';
import { buildFullPath } from '../../schema-utils';

import type { IntlShape } from 'react-intl';
import type { PropertySchema, TemplateSchema } from '../../types';

const LABEL_OVERRIDES: Record<string, string> = {
  street1: 'Street 1',
  street2: 'Street 2',
  street3: 'Street 3',
  asin: 'ASIN',
  fillSku: 'Fill SKU',
  imageUrl: 'Image URL',
  productSku: 'Product SKU',
  sku: 'SKU',
  upc: 'UPC',
  orderId: 'Order ID',
  returnUrl: 'Return URL',
  uspsZone: 'USPS Zone',
  showReturnQrCode: 'Show Return QR Code',
  showSkus: 'Show SKUs',
  logoUrl: 'Logo URL',
};

const formatLabel = (camelCase: string): string => {
  if (LABEL_OVERRIDES[camelCase]) {
    return LABEL_OVERRIDES[camelCase];
  }
  const spaced = camelCase.replace(/([A-Z])/g, ' $1');
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

type FieldNode = {
  id: string;
  value: string;
  label: string;
  description?: React.ReactNode;
  isArray: boolean;
};

type GroupNode = {
  id: string;
  label: string;
  fields: FieldNode[];
  subgroups: GroupNode[];
  isArray: boolean;
  arrayPath?: string;
};

const getFieldDescription = (translationKey: string, intl: IntlShape): string | undefined => {
  const message = fieldDescriptionMessages[translationKey as keyof typeof fieldDescriptionMessages];
  return message ? intl.formatMessage(message) : undefined;
};

const buildGroupFromSchema = (
  properties: Record<string, PropertySchema>,
  intl: IntlShape,
  pathPrefix = '',
  label = '',
  isArray = false,
  arrayPath?: string,
): GroupNode => {
  const group: GroupNode = {
    id: pathPrefix,
    label,
    fields: [],
    subgroups: [],
    isArray,
    arrayPath,
  };

  Object.entries(properties).forEach(([key, prop]) => {
    const fullPath = buildFullPath(pathPrefix, key);

    if (prop.type === 'array') {
      group.subgroups.push(
        buildGroupFromSchema(prop.itemSchema?.properties ?? {}, intl, fullPath, formatLabel(prop.translationKey), true, fullPath),
      );
    } else if (prop.type === 'object' && prop.properties) {
      group.subgroups.push(buildGroupFromSchema(prop.properties, intl, fullPath, formatLabel(prop.translationKey)));
    } else {
      group.fields.push({
        id: fullPath,
        value: fullPath,
        label: formatLabel(prop.translationKey),
        description: prop.description ?? getFieldDescription(prop.translationKey, intl),
        isArray: false,
      });
    }
  });

  return group;
};

type FieldItemProps = {
  field: FieldNode;
  onFieldSelect: (fieldValue: string, isArrayField?: boolean) => void;
};

const FieldItem = memo(({ field, onFieldSelect }: FieldItemProps) => {
  const handleFieldClick = useCallback(() => {
    onFieldSelect(field.value, field.isArray);
  }, [field.value, field.isArray, onFieldSelect]);

  return (
    <Tooltip position='top'>
      <Button className={style['sidebar-field-item']} onClick={handleFieldClick} theme={THEME_CONSTANTS.unstyled}>
        {field.label}
        {field.description ? (
          <Text className={style['sidebar-field-description']} textType={TextType.Description} value={field.description} />
        ) : null}
      </Button>
      <TooltipContent contentClassName={style['sidebar-tooltip-content']}>{`{{ ${field.value} }}`}</TooltipContent>
    </Tooltip>
  );
});

FieldItem.displayName = 'FieldItem';

type CollapsibleGroupProps = {
  group: GroupNode;
  onFieldSelect: (fieldValue: string, isArrayField?: boolean) => void;
  isOpen: boolean;
  onToggle: (groupId: string) => void;
};

const CollapsibleGroup = memo(({ group, onFieldSelect, isOpen, onToggle }: CollapsibleGroupProps) => {
  const [openSubgroupId, setOpenSubgroupId] = useState<string | null>(null);

  const handleToggle = useCallback(() => {
    onToggle(group.id);
  }, [group.id, onToggle]);

  const handleSubgroupToggle = useCallback((subgroupId: string) => {
    setOpenSubgroupId((prev) => (prev === subgroupId ? null : subgroupId));
  }, []);

  return (
    <div className={style['sidebar-subgroup']}>
      <FlexRow className={style['sidebar-subgroup-header']}>
        <Button ariaLabel='toggle' className={style['sidebar-subgroup-toggle']} onClick={handleToggle} theme={THEME_CONSTANTS.unstyled}>
          <FlexRow className={style['sidebar-subgroup-container']} gap={Gap.Space4}>
            <FontAwesomeIcon
              className={classnames(style['sidebar-subgroup-arrow'], isOpen && style['sidebar-subgroup-arrow-open'])}
              icon={faCaretRight}
            />
            <Text className={style['sidebar-subgroup-label']} textType={TextType.Label} value={group.label} />
          </FlexRow>
        </Button>
      </FlexRow>
      <div className={classnames(style['sidebar-subgroup-content'], isOpen && style['sidebar-subgroup-content-open'])}>
        <div className={style['sidebar-subgroup-content-inner']}>
          {group.subgroups
            .sort((a, b) => a.label.localeCompare(b.label))
            .map((subgroup) => (
              <CollapsibleGroup
                key={subgroup.id}
                group={subgroup}
                isOpen={openSubgroupId === subgroup.id}
                onFieldSelect={onFieldSelect}
                onToggle={handleSubgroupToggle}
              />
            ))}
          {group.fields
            .sort((a, b) => a.label.localeCompare(b.label))
            .map((field) => (
              <FieldItem key={field.id} field={field} onFieldSelect={onFieldSelect} />
            ))}
        </div>
      </div>
    </div>
  );
});

CollapsibleGroup.displayName = 'CollapsibleGroup';

type FieldReplacementSidebarProps = {
  schema: TemplateSchema;
  onFieldSelect: (fieldValue: string, isArrayField?: boolean) => void;
};

export const FieldReplacementSidebar = memo(({ schema, onFieldSelect }: FieldReplacementSidebarProps) => {
  const intl = useIntl();
  const rootGroup = useMemo(() => buildGroupFromSchema(schema.properties, intl), [schema, intl]);
  const [openGroupId, setOpenGroupId] = useState<string | null>(null);

  const handleGroupToggle = useCallback((groupId: string) => {
    setOpenGroupId((prev) => (prev === groupId ? null : groupId));
  }, []);

  return (
    <div className={style.sidebar}>
      <div className={style['sidebar-items']}>
        {rootGroup.subgroups
          .sort((a, b) => a.label.localeCompare(b.label))
          .map((group) => (
            <CollapsibleGroup
              key={group.id}
              group={group}
              isOpen={openGroupId === group.id}
              onFieldSelect={onFieldSelect}
              onToggle={handleGroupToggle}
            />
          ))}
        {rootGroup.fields
          .sort((a, b) => a.label.localeCompare(b.label))
          .map((field) => (
            <FieldItem key={field.id} field={field} onFieldSelect={onFieldSelect} />
          ))}
      </div>
    </div>
  );
});

FieldReplacementSidebar.displayName = 'FieldReplacementSidebar';
